import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import AuthService from '../../services/auth';
import RootContext from '../../services/context-states/root-context';

import { callApi } from '../../api/helpers';
import { userApi } from '../../api/openapi-axios-client';

import { Model } from 'survey-core';
import 'survey-core/defaultV2.min.css';
import { Survey } from 'survey-react-ui';
import surveyConfigs from './survey-configs';
import { returnTheme } from './survey-configs/Theme';
import { MessageResponse } from '../../types';

const AtlasSurvey: React.FC = () => {
    const { surveyKey } = useParams<{ surveyKey: string }>();
    const { userContext, messageContext } = useContext(RootContext);
    const { currentUser } = userContext;
    const { setError } = messageContext;

    const [showSurvey, setShowSurvey] = useState<boolean>(false);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const returnTo = searchParams.get('returnTo') || '/home';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const surveyJson = surveyKey ? surveyConfigs[surveyKey] : null;

    useEffect(() => {
        if (!surveyJson || !surveyKey) {
            navigate('/home', { replace: true });
        }
    }, [surveyJson, surveyKey, navigate]);

    const survey = useMemo(() => {
        if (!surveyJson) return null;
        const model = new Model(surveyJson);
        // @ts-expect-error - Ignoring type mismatch with backgroundImageFit property
        model.applyTheme(returnTheme());
        return model;
    }, [surveyJson]);

    useEffect(() => {
        if (!survey || !surveyKey) return;

        const handler = async (sender: any) => {
            try {
                await callApi<MessageResponse>(() => userApi.userSurveys.createSurvey({
                    userId: currentUser?.id || '',
                    key: surveyKey,
                    response: JSON.stringify(sender.data)
                }));
                navigate(returnTo);
            } catch (error) {
                console.error('Error saving survey response:', error);
                setError('We could not save your survey responses. Please try again.');
            }
        };

        survey.onComplete.add(handler);
        return () => { survey.onComplete.remove(handler); };
    }, [survey, surveyKey, currentUser?.id, navigate, returnTo, setError]);

    if (!surveyJson || !surveyKey || !survey) {
        return null;
    }

    const welcomeBanner = (): React.ReactElement => {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'center' : 'flex-start',
                borderRadius: '5px',
                borderColor: theme.palette.container.border,
                backgroundColor: theme.palette.container.background,
                borderWidth: '1px',
                borderStyle: 'solid',
                padding: isMobile ? 2 : 4,
                gap: isMobile ? 2 : 3,
                maxWidth: isMobile ? '90vw' : '800px',
                width: '100%'
            }}>
                <Typography sx={{fontSize: isMobile ? 36 : 48, textAlign: 'center'}}>{String.fromCodePoint(0x1F4CB)}</Typography>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.25, textAlign: isMobile ? 'center' : 'left'}}>
                    <Typography sx={{fontSize: isMobile ? 16 : 18}}>We'd love to hear how your career journey is going!</Typography>
                    <Typography sx={{fontSize: isMobile ? 16 : 18}}>This quick check-in helps us understand where you are and how we can help.</Typography>
                    <Button
                        variant='contained'
                        color='primary'
                        type='submit'
                        sx={{
                            width: isMobile ? '100%' : 150,
                            marginTop: 3,
                            alignSelf: isMobile ? 'stretch' : 'flex-start'
                        }}
                        onClick={() => {
                            if(!currentUser || !currentUser.id || !AuthService.getCurrentUser()?.token) {
                                navigate('/');
                                return;
                            }
                            setShowSurvey(true)
                        }}
                        aria-label="Start survey">
                        Start Survey
                    </Button>
                </Box>
            </Box>
        )
    }

    return (
        <Container
            maxWidth={isMobile ? false : "lg"}
            disableGutters={isMobile}
            sx={{
                px: { xs: 2, sm: 3, md: 3 },
                py: { xs: 2, sm: 3, md: 4 },
                width: '100%'
            }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: isMobile ? 4 : 12,
                padding: isMobile ? 1 : 2,
                minHeight: isMobile ? 'calc(100vh - 200px)' : 'auto'
            }}>
                {!showSurvey ? welcomeBanner() :
                    <Box sx={{
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '800px',
                        '& .sv-root-modern': {
                            padding: isMobile ? '8px' : '16px'
                        }
                    }}>
                        <Survey model={survey} />
                    </Box>
                }
            </Box>
        </Container>
    );
};

export default AtlasSurvey;
